import { useState } from "react";
import ResourcePage from "@/components/ResourcePage";
import PageHeader from "@/components/PageHeader";
import { CLIENT_STATUSES, PAYMENT_STATUSES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fields = [
  { name: "groom", label: "Nama Pengantin Pria", required: true },
  { name: "bride", label: "Nama Pengantin Wanita", required: true },
  { name: "phone", label: "No. WhatsApp", type: "tel" },
  { name: "email", label: "Email", type: "email" },
  { name: "address", label: "Alamat", full: true },
  { name: "parent_name", label: "Nama Orang Tua" },
  { name: "parent_phone", label: "Kontak Orang Tua" },
  { name: "booking_date", label: "Tanggal Booking", type: "date" },
  { name: "wedding_date", label: "Tanggal Wedding", type: "date" },
  { name: "venue", label: "Venue" },
  { name: "package", label: "Paket" },
  { name: "total_price", label: "Total Harga", type: "number" },
  { name: "status", label: "Status", type: "select", options: CLIENT_STATUSES, default: "Lead" },
  { name: "payment_status", label: "Status Pembayaran", type: "select", options: PAYMENT_STATUSES, default: "UNPAID" },
  { name: "pic", label: "PIC" },
  { name: "notes", label: "Catatan", type: "textarea", full: true },
];

const columns = [
  { key: "groom", label: "Pengantin", render: (r) => <span className="font-semibold text-[#1A1A1A]">{r.groom} & {r.bride}</span> },
  { key: "phone", label: "WhatsApp" },
  { key: "wedding_date", label: "Tanggal Wedding", date: true },
  { key: "package", label: "Paket" },
  { key: "total_price", label: "Total", money: true },
  { key: "status", label: "Status", badge: true },
  { key: "payment_status", label: "Bayar", badge: true },
];

export default function Clients() {
  const { can } = useAuth();
  const [acc, setAcc] = useState(null);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (pw.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/clients/${acc.id}/account`, { password: pw });
      toast.success(`Akun login client dibuat: ${r.data.email}`);
      setAcc(null); setPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const rowActions = can(3) ? (row) => (
    <Button size="icon" variant="ghost" className="h-8 w-8 text-[#7A7A7A] hover:text-emerald-600" title="Buat akun login client" onClick={() => { setAcc(row); setPw(""); }} data-testid="client-account-btn"><KeyRound className="w-4 h-4" /></Button>
  ) : undefined;

  return (
    <>
      <ResourcePage
        resource="clients" title="Client" writeLevel={3}
        columns={columns} fields={fields} rowActions={rowActions}
        PageHeaderComp={<PageHeader overline="Data Calon Pengantin" title="Clients" subtitle="Kelola data client dan buatkan akun login portal untuk mereka." />}
      />
      <Dialog open={!!acc} onOpenChange={(o) => !o && setAcc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">Akun Login Client</DialogTitle></DialogHeader>
          {acc && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-[#4A4A4A]">Buat/atur ulang akun login portal untuk <b>{acc.groom} & {acc.bride}</b>.</p>
              <div><Label className="text-xs font-semibold mb-1 block">Email (dari data client)</Label><Input value={acc.email || ""} disabled className="bg-beige/40" data-testid="client-account-email" /></div>
              {!acc.email && <p className="text-xs text-red-600">Email client kosong — lengkapi data client dulu.</p>}
              <div><Label className="text-xs font-semibold mb-1 block">Password</Label><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min. 6 karakter" data-testid="client-account-password" /></div>
              <p className="text-[11px] text-[#7A7A7A]">Bagikan email & password ini ke client. Mereka juga bisa mengganti password sendiri via "Lupa password".</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcc(null)}>Batal</Button>
            <Button onClick={submit} disabled={saving || !acc?.email} data-testid="client-account-submit" className="bg-burgundy hover:bg-burgundy-dark text-white">Simpan Akun</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
